import { useMemo, useState } from 'react';

import { useAppSelector } from 'redux/store';
import useAdvanceTable from 'hooks/useAdvanceTable';
import { useTableController } from 'hooks/useTableController';
import { folderColumn } from 'helpers/folderPaths';
import TableViewLayout from 'layouts/TableViewLayout';
import { getActions, ActionButton, ActionDropdown, type ActionEntity } from 'actions';

import { fetchPythonLibraries } from '../../../../redux/slices/pythonLibrariesSlice';

const toActionEntity = (row: any): ActionEntity => ({ type: 'python_library', id: row.python_library_id, name: row.name });

const ListPythonLibraries = () => {
  const { items: libraries, loading, error } = useAppSelector((state) => state.pythonLibraries);
  const userData = useAppSelector((state) => state.auth.userData);

  const pageName = 'python-libraries';
  const [showSidebar, setShowSidebar] = useState(false);

  const headerConfig = useMemo(
    () => [
      { header_key: 'name', label: 'Name' },
      { header_key: 'filename', label: 'Filename' },
      { header_key: 'description', label: 'Description' },
      { header_key: 'hash_short', label: 'Hash' },
      { header_key: 'created_at', label: 'Created' },
      { header_key: 'updated_at', label: 'Updated' },
    ],
    []
  );
  const filtersConfig: any[] = [];

  const {
    filters,
    setFilterAndSync,
    resetFilters,
    updateSort,
    updatePage,
    updateLimit,
    limit,
    page,
    updateUserSettings,
    getFormattedData,
    generateColumns,
    refresh,
    isSilentRefresh,
  } = useTableController({
    userData,
    pageName,
    filtersConfig,
    headerConfig,
    fetchData: fetchPythonLibraries as any,
    columnFormats: { created_at: 'datetime', updated_at: 'datetime' },
    getFormattedRow: (row: any) => ({
      ...row,
      hash_short: row.hash ? `${row.hash.substring(0, 12)}...` : '',
    }),
  });

  const isLoading = loading.fetchPythonLibraries;

  const actionsColumn = {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }: any) => {
      const actions = getActions({ entities: [toActionEntity(row.original)], refresh });
      return <ActionDropdown actions={actions} items={['edit', 'addToTransportPythonLibrary', 'delete']} />;
    },
  };

  const baseColumns = generateColumns((key: string, order: 'asc' | 'desc') => {
    updateSort(key, order);
    updateUserSettings(key, order);
  });
  const columns = [...baseColumns, folderColumn, actionsColumn];
  const formattedData = useMemo(() => getFormattedData(libraries || []), [libraries]);

  const table = useAdvanceTable({
    data: formattedData,
    columns,
    pageSize: limit,
    pagination: true,
    sortable: true,
    selection: true,
    getRowId: (row) => String(row.python_library_id),
  });

  const totalItems = libraries?.length || 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / (limit || 1)));

  const selectionEntities = table.getSelectedRowModel().rows.map((r: any) => toActionEntity(r.original));
  const toolbarActions = getActions({ entities: selectionEntities, refresh });

  return (
    <div className="p-4 d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
      <h4 className="mb-3 flex-shrink-0">Python Libraries</h4>
      <TableViewLayout
        filtersConfig={filtersConfig}
        filters={filters}
        pageName={pageName}
        columns={headerConfig.map((c) => ({
          key: c.header_key,
          label: c.label,
        }))}
        pageSize={limit}
        error={error}
        onFilterChange={setFilterAndSync}
        onResetFilters={resetFilters}
        onPageSizeChange={updateLimit}
        currentPage={page}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={updatePage}
        table={table}
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
        onRefresh={refresh}
        isLoading={isLoading}
        isSilentRefresh={isSilentRefresh}
        toolbarButtons={
          <>
            <ActionButton actions={toolbarActions} action="createPythonLibrary" variant="primary" />
            <ActionButton
              actions={toolbarActions}
              action="addToTransportPythonLibrary"
              className="ms-2"
              variant="secondary"
              label="Add selected to transport"
            />
          </>
        }
      />
    </div>
  );
};

export default ListPythonLibraries;
